import { HashRouter as Router, Routes, Route, Navigate, useLocation, useNavigate, useHref } from 'react-router-dom';
import { useEffect, useState, lazy, Suspense, Component, type ErrorInfo, type ReactNode } from 'react';
import { Toast } from './components/Toast';
import { initializeLocalDB, initializeCollections, preloadAllData, startAutoSync, getRemoteApiUrl } from './utils/db';
import { UpdateModal } from './components/UpdateModal';
// H5 移动端外壳（顶部标题栏 + 底部 5-Tab 导航，真实页面统一包裹）
import H5Shell from './components/H5Shell';
import { runtime } from '@/utils/runtime';
// FIXED: 引入 AuthProvider，为人员管理权限系统提供全局上下文
import { AuthProvider, useAuth, type AuthUser, type MerchantConfig } from './utils/auth';
// FIXED: 阶段5 - 路由级权限守卫所需模块定义
import { MODULES } from './utils/permission';
import type { PermissionConfig, Role } from './utils/permission';

// 懒加载页面组件
const Sidebar = lazy(() => import('./components/Sidebar'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const HomePage = lazy(() => import('./pages/HomePage'));
const InfluencersPage = lazy(() => import('./pages/InfluencersPage'));
// FIXED: 达人黑名单列表页懒加载
const InfluencerBlacklistPage = lazy(() => import('./pages/InfluencerBlacklistPage'));
// 全局黑名单（跨商家共享）懒加载
const GlobalBlacklistPage = lazy(() => import('./pages/GlobalBlacklistPage'));
// FIXED: 数据管理4个页面懒加载
const StaffSamplesPage = lazy(() => import('./pages/data-management/StaffSamplesPage'));
const WorkgroupSamplesPage = lazy(() => import('./pages/data-management/WorkgroupSamplesPage'));
const ExpressStatusPage = lazy(() => import('./pages/data-management/ExpressStatusPage'));
// FIXED: 样品跟进数据页（数据管理下的统计视图）
const SampleProgressDataPage = lazy(() => import('./pages/data-management/SampleProgressDataPage'));
const ProductsPage = lazy(() => import('./pages/ProductsPage'));
const ExpressPage = lazy(() => import('./pages/ExpressPage'));
const SamplesPage = lazy(() => import('./pages/SamplesPage'));
const PosterPage = lazy(() => import('./pages/PosterPage'));
const HandCardPage = lazy(() => import('./pages/HandCardPage'));
const InitDB = lazy(() => import('./pages/InitDB'));
const WebhookPage = lazy(() => import('./pages/settings/WebhookPage'));
const TemplatePage = lazy(() => import('./pages/settings/TemplatePage'));
const SmsPage = lazy(() => import('./pages/settings/SmsPage'));
// ADDED: 资费管理（在线充值）
const FeePage = lazy(() => import('./pages/settings/FeePage'));
const SystemPage = lazy(() => import('./pages/settings/SystemPage'));
// FIXED: 人员管理页面懒加载
const StaffPage = lazy(() => import('./pages/settings/StaffPage'));
// FIXED: 商家信息配置页已移除（功能迁移至左下角账号弹窗）
const WorkgroupsPage = lazy(() => import('./pages/settings/WorkgroupsPage'));
const PermissionsPage = lazy(() => import('./pages/settings/PermissionsPage'));
// FIXED: 样品进度页面
const SampleProgressPage = lazy(() => import('./pages/SampleProgressPage'));
// P1-B3: 投产看板页面
const InvestmentDashboardPage = lazy(() => import('./pages/data-management/InvestmentDashboardPage'));
// P1-B4: 员工绩效页面
const EmployeePerformancePage = lazy(() => import('./pages/data-management/EmployeePerformancePage'));
// 发样审批工作台
const ApprovalWorkbenchPage = lazy(() => import('./pages/ApprovalWorkbenchPage'));
// 打单工作台
const PrintingWorkbenchPage = lazy(() => import('./pages/PrintingWorkbenchPage'));
// 第三期：样品回收管理
const SampleReturnPage = lazy(() => import('./pages/SampleReturnPage'));
// 第三期：全局风险预警中心
const RiskAlertPage = lazy(() => import('./pages/RiskAlertPage'));
// 第三期：工作组预算管理
const BudgetManagePage = lazy(() => import('./pages/BudgetManagePage'));
// 消息通知设置
const NotificationPage = lazy(() => import('./pages/settings/NotificationPage'));
// 账号绑定（微信扫码绑定）
const AccountBindPage = lazy(() => import('./pages/settings/AccountBindPage'));
// 系统设置容器页（Tab 容器）
const AccountManagePage = lazy(() => import('./pages/settings/AccountManagePage'));
const MessageCenterPage = lazy(() => import('./pages/settings/MessageCenterPage'));
const SystemOpsPage = lazy(() => import('./pages/settings/SystemOpsPage'));
// H5 Mock 页面（企微审核截图专用，纯静态数据，无真实接口）
const MockWorkbenchPage = lazy(() => import('./pages/h5/MockWorkbenchPage'));
const MockApprovalPage = lazy(() => import('./pages/h5/MockApprovalPage'));
const MockSettingsPage = lazy(() => import('./pages/h5/MockSettingsPage'));
// H5 真实绑定页（企微 OAuth 未绑定时使用）
const H5BindPage = lazy(() => import('./pages/h5/H5BindPage'));
// H5 真实移动端页面（企微登录后使用，真实 API 调用，替换原 Mock 静态页）
const H5WorkbenchPage = lazy(() => import('./pages/h5/H5WorkbenchPage'));
const H5SamplesPage = lazy(() => import('./pages/h5/H5SamplesPage'));
const H5ApprovalPage = lazy(() => import('./pages/h5/H5ApprovalPage'));
const H5DataPage = lazy(() => import('./pages/h5/H5DataPage'));
const H5ProfilePage = lazy(() => import('./pages/h5/H5ProfilePage'));

/**
 * 企微入口路由解析器（从高到低优先级）：
 *  1) window.__H5_INIT_PAGE__  —— 后端在 /api/workwx/homepage 注入，最权威
 *  2) URL pathname /api/workwx/homepage /approval /settings
 *  3) URL ?page=workbench|approval|settings  —— 本地截图预览用
 *  4) URL hash #/h5/xxx                         —— 已有路由不动
 * 识别出 page 后，自动把 hash 写成 #/h5/xxx 让 React HashRouter 匹配到正确路由，
 * 并自动启用 h5-mode class + 移动端 viewport。
 */
function resolveH5Page(): { page: string | null; forceH5Mode: boolean } {
  try {
    // 1) 注入的全局变量
    const g = (window as any);
    const injected: string | undefined = g.__H5_INIT_PAGE__;
    if (injected === 'workbench' || injected === 'approval' || injected === 'settings') {
      return { page: injected, forceH5Mode: true };
    }

    // 2) pathname 匹配
    const pathname = (window.location.pathname || '').toLowerCase();
    if (/\/api\/workwx\/homepage(\/|$)/.test(pathname)) return { page: 'workbench', forceH5Mode: true };
    if (/\/api\/workwx\/approval(\/|$)/.test(pathname)) return { page: 'approval',  forceH5Mode: true };
    if (/\/api\/workwx\/settings(\/|$)/.test(pathname)) return { page: 'settings',  forceH5Mode: true };
    if (/\/api\/workwx(\/|$)/.test(pathname))         return { page: 'workbench', forceH5Mode: true };

    // 3) ?page=xxx
    const sp = new URLSearchParams(window.location.search);
    const p = (sp.get('page') || '').toLowerCase();
    if (p === 'workbench' || p === 'approval' || p === 'settings') return { page: p, forceH5Mode: true };
    if (p) {
      const alias: Record<string, string> = {
        dashboard: 'workbench',
        investment: 'approval',
        performance: 'approval',
        staff: 'settings',
        workgroup: 'settings',
      };
      if (alias[p]) return { page: alias[p], forceH5Mode: true };
    }

    // 4) hash 已经是 #/h5/xxx
    const hash = window.location.hash || '';
    if (hash.startsWith('#/h5/workbench')) return { page: 'workbench', forceH5Mode: true };
    if (hash.startsWith('#/h5/approval'))  return { page: 'approval',  forceH5Mode: true };
    if (hash.startsWith('#/h5/settings'))  return { page: 'settings',  forceH5Mode: true };
    if (hash.startsWith('#/h5/'))          return { page: null,       forceH5Mode: true };

    return { page: null, forceH5Mode: false };
  } catch (_) {
    return { page: null, forceH5Mode: false };
  }
}

/**
 * 企微审核预览专用：
 * 读取入口来源，自动跳转 #/h5/xxx Hash 路由。
 * 无需 OAuth、无需登录，纯静态 Mock 数据，供截图上传审核。
 */
function H5MockPageRouter(): null {
  useEffect(() => {
    try {
      const { page, forceH5Mode } = resolveH5Page();
      const hash = window.location.hash || '';
      const alreadyOnH5Route = hash.startsWith('#/h5/');

      if (page && (!alreadyOnH5Route || !hash.startsWith(`#/h5/${page}`))) {
        // 确保进入目标页的 hash 路由（不刷新页面的直接改 hash）
        window.location.hash = `#/h5/${page}`;
      }

      if (forceH5Mode) {
        document.body.classList.add('h5-mode');
        const vp = document.querySelector('meta[name="viewport"]') as HTMLMetaElement | null
          || (() => {
              const m = document.createElement('meta');
              m.name = 'viewport';
              document.head.appendChild(m);
              return m;
            })();
        vp.content = 'width=device-width,initial-scale=1,maximum-scale=1,user-scalable=no,viewport-fit=cover';
      }
    } catch (_) {}
  }, []);
  return null;
}

/**
 * H5 Mock 页面外壳：
 * - 自动 h5-mode class
 * - 顶部标题栏 + 底部 3-Tab
 */
function H5MockShell({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    document.body.classList.add('h5-mode');
    const vp = document.querySelector('meta[name="viewport"]') as HTMLMetaElement | null
      || (() => {
          const m = document.createElement('meta');
          m.name = 'viewport';
          document.head.appendChild(m);
          return m;
        })();
    vp.content = 'width=device-width,initial-scale=1,maximum-scale=1,user-scalable=no,viewport-fit=cover';
    return () => {};
  }, []);

  const tabs = [
    { key: 'workbench', path: '/h5/workbench', label: '工作台',
      icon: (<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 12l9-9 9 9"/><path d="M5 10v10h14V10"/></svg>) },
    { key: 'approval',  path: '/h5/approval',  label: '审批',
      icon: (<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>) },
    { key: 'settings',  path: '/h5/settings',  label: '设置',
      icon: (<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="8" r="4"/><path d="M4 21c0-4.4 3.6-8 8-8s8 3.6 8 8"/></svg>) },
  ];
  const current = location.pathname;
  const title = tabs.find(t => current.startsWith(t.path))?.label || '企微 H5';

  return (
    <div style={{ minHeight: '100vh', background: 'var(--apple-bg)' }}>
      {/* 顶部导航栏 */}
      <div style={{
        position: 'sticky', top: 0, zIndex: 50,
        background: 'rgba(255,255,255,0.94)',
        backdropFilter: 'saturate(180%) blur(20px)',
        WebkitBackdropFilter: 'saturate(180%) blur(20px)',
        borderBottom: '0.5px solid var(--apple-separator)',
        padding: '11px 14px',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 14, fontWeight: 600,
      }}>{title}</div>

      {/* 主体内容 */}
      <Suspense fallback={<PageLoader />}>{children}</Suspense>

      {/* 底部 3-Tab */}
      <nav className="h5-bottom-nav" style={{ position: 'fixed' }}>
        {tabs.map(t => {
          const active = current.startsWith(t.path);
          return (
            <button
              key={t.key}
              type="button"
              className={`h5-tab-item ${active ? 'active' : ''}`}
              onClick={() => navigate(t.path)}
              style={{ color: active ? '#0071e3' : 'var(--apple-gray)' }}
            >
              <div className="h5-tab-icon">{t.icon}</div>
              <div className="h5-tab-label">{t.label}</div>
            </button>
          );
        })}
      </nav>
    </div>
  );
}

// 加载中组件
function PageLoader() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: 'var(--apple-bg)' }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ width: 32, height: 32, border: '3px solid var(--apple-separator)', borderTopColor: 'var(--apple-blue)', borderRadius: '50%', animation: 'spin 0.7s linear infinite', margin: '0 auto 12px' }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        <p style={{ color: 'var(--apple-gray)', fontSize: 13 }}>加载中...</p>
      </div>
    </div>
  );
}

// ======== 企微 OAuth2 对接辅助函数 ========
// 后端 role 转客户端 Role（与 LoginPage 保持一致）
function backendRoleToClient(backendRole: string): Role {
  if (backendRole === 'admin') return 'admin';
  if (backendRole === 'leader') return 'leader';
  if (backendRole === 'merchant') return 'admin'; // 商家=本客户端管理员
  return 'staff';
}

// 拉取商家配置（merchantConfig），用于登录态初始化
async function fetchMerchantConfig(token: string): Promise<MerchantConfig | undefined> {
  try {
    const base = getRemoteApiUrl();
    const res = await fetch(`${base}/api/merchant/config`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) return undefined;
    const data = await res.json().catch(() => ({}));
    const cfg = data?.config || data;
    if (!cfg) return undefined;
    return {
      expireDate: cfg.expireDate || '',
      staffQuota: String(cfg.staffQuota ?? '0'),
      permissions: (cfg.permissions || null) as PermissionConfig | null,
      enabled: cfg.enabled !== false,
      company_name: cfg.company_name || '',
      displayName: cfg.displayName || '',
      configVersion: Number(cfg.configVersion) || 0,
    };
  } catch (_) {
    return undefined;
  }
}

// 把后端 /auth 或 /bind 返回的 user + token 转成 AuthUser 并调用 setH5Auth
async function applyBackendAuth(
  setH5Auth: (info: any) => void,
  token: string,
  backendUser: any,
  displayNameFallback?: string,
  workwxUserid?: string
): Promise<AuthUser> {
  const role = backendRoleToClient(String(backendUser?.role || 'staff'));
  const name = backendUser?.name || displayNameFallback || backendUser?.username || '企微用户';
  const accountId = backendUser?.employee_no || backendUser?.employeeId || backendUser?.employee_id || backendUser?.id || backendUser?.username || name;
  const merchantCfg = await fetchMerchantConfig(token);
  const permissions = merchantCfg?.permissions || backendUser?.permissions || null;
  const authUser: AuthUser = {
    employeeId: String(accountId),
    employee_no: backendUser?.employee_no || undefined,
    merchant_id: backendUser?.merchant_id || undefined,
    name,
    role,
    groupId: backendUser?.group_id || backendUser?.groupId || null,
    group_id: backendUser?.group_id || backendUser?.groupId || null,
    permissions,
    merchantConfig: merchantCfg,
  };
  // 存储 workwx_userid 到 localStorage（供 H5ProfilePage 读取）
  if (workwxUserid) {
    try { localStorage.setItem('workwx_userid', workwxUserid); } catch (_) {}
  }
  setH5Auth({ token, user: authUser });
  return authUser;
}

// 从 URL 中提取企微 OAuth 回调参数（后端 302 带过来的）
function extractWorkWxParams(): { token: string; bind: string; workwx_userid: string; workwx_corp_id: string; workwx_name: string; workwx_avatar: string; page: string; merchant_hint: string; } {
  try {
    const sp = new URLSearchParams(window.location.search);
    return {
      token: (sp.get('token') || '').toString(),
      bind: (sp.get('bind') || '').toString(),
      workwx_userid: (sp.get('workwx_userid') || '').toString(),
      workwx_corp_id: (sp.get('workwx_corp_id') || sp.get('corp_id') || '').toString(),
      workwx_name: decodeURIComponent((sp.get('workwx_name') || sp.get('name') || '').toString()),
      workwx_avatar: decodeURIComponent((sp.get('workwx_avatar') || sp.get('avatar') || '').toString()),
      page: (sp.get('page') || 'workbench').toString(),
      merchant_hint: (sp.get('merchant_hint') || '').toString(),
    };
  } catch (_) {
    return { token: '', bind: '', workwx_userid: '', workwx_corp_id: '', workwx_name: '', workwx_avatar: '', page: 'workbench', merchant_hint: '' };
  }
}

// FIXED: 阶段5 - 路径权限映射（与 Sidebar 菜单可见性规则保持一致）
// 未配置的路径（如首页 '/'）默认放行
interface PathPermission {
  module?: string;        // 需要的业务模块（用 canSeeMenu 判断）
  adminOnly?: boolean;    // 仅管理员可访问
  leaderCanSee?: boolean; // 组长可访问（用于工作组管理只读视图）
}

const PATH_PERMISSIONS: Record<string, PathPermission> = {
  '/products': { module: MODULES.PRODUCTS },
  '/products/poster': { module: MODULES.PRODUCTS },
  '/products/handcard': { module: MODULES.PRODUCTS },
  '/express': { module: MODULES.EXPRESS },
  '/samples': { module: MODULES.SAMPLES },
  '/approval': { module: MODULES.APPROVAL },
  '/printing': { module: MODULES.PRINTING },
  '/influencers': { module: MODULES.INFLUENCERS },
  '/influencers/list': { module: MODULES.INFLUENCERS },
  '/influencers/blacklist': { module: MODULES.INFLUENCERS },
  '/influencers/global-blacklist': { module: MODULES.INFLUENCERS },
  // FIXED: 数据管理 - 所有角色可见（不设权限限制，数据范围由页面内 filterDataByScope 控制）
  '/data-management/staff-samples': {},
  '/data-management/workgroup-samples': {},
  '/data-management/express-status': {},
  // FIXED: 样品跟进数据 - 所有角色可见（数据范围由页面内 filterDataByScope 控制：admin全部/leader本组/staff自己）
  '/data-management/sample-progress-data': {},
  // FIXED: 样品进度 - 所有角色可见（数据范围由页面内 filterDataByScope 控制）
  '/sample-progress': { module: MODULES.SAMPLE_PROGRESS },
  // 第三期：样品回收 - 所有角色可见（数据范围由页面内 filterDataByScope 控制）
  '/sample-returns': { module: MODULES.RETURNS },
  // 第三期：风险预警 - 所有角色可见
  '/risk-alerts': { module: MODULES.RISK },
  // 第三期：预算管理 - 仅管理员
  '/data-management/budget': { adminOnly: true },
  '/init-db': { adminOnly: true },
  // 新容器页路由权限
  '/settings/account': {}, // 所有角色可见，Tab 内按角色过滤
  '/settings/messages': { adminOnly: true },
  '/settings/system-ops': { adminOnly: true },
  '/settings/fee': { adminOnly: true },
  // 旧路由保留兼容（重定向到新容器页，防书签失效）
  '/settings/staff': { adminOnly: true },
  '/settings/workgroups': { leaderCanSee: true },
  '/settings/permissions': { adminOnly: true },
  '/settings/webhook': { adminOnly: true },
  '/settings/templates': { adminOnly: true },
  '/settings/sms': { adminOnly: true },
  '/settings/audit-logs': { adminOnly: true },
  '/settings/system': { adminOnly: true },
  '/settings/notification': { adminOnly: true },
  '/settings/account-bind': {}, // 所有登录用户都可以看到，自己绑定自己的微信
};

function ProtectedLayout({ children }: { children: React.ReactNode }) {
  const [dbReady, setDbReady] = useState(false);
  const [workWxBooting, setWorkWxBooting] = useState<boolean | null>(null); // null=未检测, true=处理中, false=完成
  const location = useLocation();
  const navigate = useNavigate();
  // FIXED: 阶段5 - 路由级权限守卫；H5 免登
  const { user, isAdmin, isLeader, canSeeMenu, h5Mode, setH5Auth, signedIn } = useAuth();

  // =====================================================
  //  ⚠️  所有 hooks（useState/useEffect/useXxx）必须出现在  任何 早 return 之 前
  //     否则 React 18 会抛 Minified React error #310 = Rendered more hooks than previous render.
  // =====================================================

  // ====== 企微 OAuth 回调启动流程（H5）：在任何组件挂载前执行一次 ======
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const params = extractWorkWxParams();
        // 只有带 workwx_userid / bind=1 / token 等企微标识的 URL 才进入此流程
        const hasOauthMark = !!(params.token || params.bind || params.workwx_userid ||
          new URLSearchParams(window.location.search).has('page'));
        if (!hasOauthMark) {
          if (!cancelled) setWorkWxBooting(false);
          return;
        }
        if (!cancelled) setWorkWxBooting(true);

        // 1) URL 直接带 token（后端 OAuth callback 已绑定 → 302 带 token 跳转 H5）
        if (params.token) {
          // 先用 token 设置 h5-mode，再解析用户
          if (!h5Mode) {
            document.body.classList.add('h5-mode');
          }
          // 解析 JWT payload 作为 user 的基础数据（避免再跑一圈查询）
          let jwtUser: any = null;
          try {
            const parts = params.token.split('.');
            if (parts.length === 3) {
              const payload = JSON.parse(decodeURIComponent(escape(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')))));
              jwtUser = {
                id: payload.id,
                username: payload.username,
                role: payload.role,
                merchant_id: payload.merchant_id,
                employee_no: payload.employee_no,
                group_id: payload.group_id,
                // name/displayName 将在 applyBackendAuth 中结合 workwx_name / 商家名 填入
              };
            }
          } catch (_) { jwtUser = null; }
          const displayName = decodeURIComponent((new URLSearchParams(window.location.search).get('name') || '').toString()) ||
            (jwtUser?.username || '');
          if (jwtUser) {
            jwtUser.name = displayName;
          }
          const baseUser = jwtUser || { username: displayName, role: 'merchant', name: displayName };
          await applyBackendAuth(setH5Auth, params.token, baseUser, displayName, params.workwx_userid);

          // 跳到目标页（清理 URL 上的敏感参数，避免刷新时重复处理）
          try {
            const page = params.page || 'workbench';
            const targetHash = `#/h5/${page}`;
            if (!window.location.hash.startsWith(targetHash)) {
              window.location.hash = targetHash;
            }
            // 清除 URL query 参数（只保留 hash）
            const cleanUrl = window.location.pathname + window.location.hash;
            window.history.replaceState({}, '', cleanUrl);
          } catch (_) {}
          if (!cancelled) setWorkWxBooting(false);
          return;
        }

        // 2) bind=1 或 workwx_userid 已带（未绑定路径）：调用后端 /api/h5/workwx/auth 真实查询
        if (params.workwx_userid || params.bind === '1') {
          if (!h5Mode) {
            document.body.classList.add('h5-mode');
          }
          // 先进入 H5 模式
          setH5Auth({ token: '' }); // 仅激活模式态，不设登录态
          try {
            const base = getRemoteApiUrl();
            const q = new URLSearchParams({
              workwx_userid: params.workwx_userid,
              corp_id: params.workwx_corp_id,
              name: encodeURIComponent(params.workwx_name),
              avatar: encodeURIComponent(params.workwx_avatar),
            });
            const authResp = await fetch(`${base}/api/h5/workwx/auth?${q.toString()}`);
            const data = await authResp.json().catch(() => ({}));
            if (data?.bound && data?.token) {
              // 已绑定：直接落地身份
              await applyBackendAuth(setH5Auth, data.token, data.user, params.workwx_name, params.workwx_userid);
              const page = params.page || 'workbench';
              const targetHash = `#/h5/${page}`;
              if (!window.location.hash.startsWith(targetHash)) {
                window.location.hash = targetHash;
              }
              try {
                const cleanUrl = window.location.pathname + window.location.hash;
                window.history.replaceState({}, '', cleanUrl);
              } catch (_) {}
              if (!cancelled) setWorkWxBooting(false);
              return;
            } else {
              // 未绑定：把参数暂存到 sessionStorage，跳到绑定页
              const store = {
                workwx_userid: params.workwx_userid,
                workwx_corp_id: params.workwx_corp_id,
                workwx_name: params.workwx_name,
                workwx_avatar: params.workwx_avatar,
                merchant_hint: params.merchant_hint,
                page: params.page || 'workbench',
              };
              sessionStorage.setItem('h5_bind_params', JSON.stringify(store));
              // 清理 URL 参数后跳 /h5/bind
              try { window.history.replaceState({}, '', window.location.pathname); } catch (_) {}
              navigate('/h5/bind', { replace: true });
              if (!cancelled) setWorkWxBooting(false);
              return;
            }
          } catch (e) {
            console.error('[企微H5] auth 接口异常:', e);
            const store = {
              workwx_userid: params.workwx_userid,
              workwx_corp_id: params.workwx_corp_id,
              workwx_name: params.workwx_name,
              workwx_avatar: params.workwx_avatar,
              merchant_hint: params.merchant_hint,
              page: params.page || 'workbench',
            };
            sessionStorage.setItem('h5_bind_params', JSON.stringify(store));
            navigate('/h5/bind', { replace: true });
          }
        }
      } catch (e) {
        console.error('[企微H5] 启动流程异常:', e);
      }
      if (!cancelled) setWorkWxBooting(false);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // 自动检测 H5 模式：1) URL 带 page/route 参数（企微 OAuth 302 过来）；2) __H5_TOKEN__；3) 移动端 UA
    try {
      let token = '';
      const searchParams = new URLSearchParams(window.location.search);
      const hasH5Param = searchParams.has('page') || searchParams.has('route');
      if (searchParams.has('token')) {
        token = searchParams.get('token') || '';
      } else if ((window as any).__H5_TOKEN__) {
        token = (window as any).__H5_TOKEN__;
      }
      const isMobileUA = /Android|iPhone|iPad|iPod|MicroMessenger|WeChat/i.test(navigator.userAgent || '');
      if (token || hasH5Param || isMobileUA) {
        if (!h5Mode) {
          console.log('[H5免登] 触发 H5 模式:', { token: !!token, hasH5Param, isMobileUA });
          document.body.classList.add('h5-mode');
        }
        if (token && !signedIn) setH5Auth({ token });
        else if (!h5Mode) setH5Auth({ token: '' }); // 仅激活模式态
      }
    } catch (e) {
      console.warn('[H5免登] 检测异常（忽略）:', e);
    }
  }, [h5Mode, setH5Auth, signedIn]);

  useEffect(() => {
    // h5Mode 变化同步 body class 与视口
    try {
      if (h5Mode) {
        document.body.classList.add('h5-mode');
        let vp = document.querySelector('meta[name="viewport"]') as HTMLMetaElement | null;
        if (!vp) {
          vp = document.createElement('meta');
          vp.name = 'viewport';
          document.head.appendChild(vp);
        }
        vp.content = 'width=device-width,initial-scale=1,maximum-scale=1,user-scalable=no,viewport-fit=cover';
      } else {
        document.body.classList.remove('h5-mode');
      }
    } catch (_) {}
  }, [h5Mode]);

  useEffect(() => {

    const initDb = async () => {
      try {
        if (!runtime.isWeb) console.log('🚀 初始化本地数据库...');
        await initializeLocalDB();
        if (!runtime.isWeb) console.log('✅ 数据库初始化完成');
        setDbReady(true);
        startAutoSync().catch(e => console.error('❌ 自动同步失败:', e));
        // 初始化通知服务
        try {
          const { initNotificationService } = await import('./utils/notification');
          initNotificationService(isAdmin ? 'admin' : (isLeader ? 'leader' : 'staff'), user?.employeeId || '', user?.name || '');
        } catch (e) {
          console.error('[通知] 初始化失败:', e);
        }
      } catch (e) {
        console.error('❌ 数据库初始化失败:', e);
        setDbReady(true);
      }
    };
    initDb();
  }, []);

  // =================== 以下为 条件早 return 段（必须放 ALL hooks 之后） ===================

  // --- 桌面端登录守卫：H5 模式之外，未登录强制跳 /login，带上 redirect 以便登录成功后回跳 ---
  //     注：必须放在所有 useEffect 之后，避免 hooks 数量不一致（#310）
  if (!h5Mode && !signedIn) {
    const from = location.pathname + location.search + location.hash;
    const target = from && from !== '/' && from !== '/login'
      ? `/login?redirect=${encodeURIComponent(from)}`
      : '/login';
    return <Navigate to={target} replace />;
  }

  // FIXED: 阶段5 - 权限检查：无权限访问的路径重定向到首页
  // 规则与 Sidebar isItemVisible 一致，防止用户通过直接输入URL绕过菜单
  const checkAccess = (path: string): boolean => {
    const perm = PATH_PERMISSIONS[path];
    if (!perm) return true; // 未配置的路径（如首页）默认放行
    if (isAdmin) return true;
    if (perm.adminOnly) return false;
    if (perm.leaderCanSee && isLeader) return true;
    if (perm.leaderCanSee && !isLeader) return false; // 员工不可访问
    if (perm.module && !canSeeMenu(perm.module)) return false;
    return true;
  };

  if (!checkAccess(location.pathname)) {
    console.warn(`[权限守卫] 无权限访问 ${location.pathname}，重定向到首页`);
    return <Navigate to="/" replace />;
  }

  // 企微 OAuth 启动流程尚未完成时，先展示加载屏
  if (workWxBooting === true) return <PageLoader />;

  if (!dbReady) return <PageLoader />;

  return (
    <Suspense fallback={<PageLoader />}>
      {!h5Mode && <Sidebar h5Mode={h5Mode} />}
      <main className="main-content">{children}</main>
    </Suspense>
  );
}

// ===== 审核专用：判定是否为审核演示站点 =====
// 严格双重守卫：仅 x.dtuitui.com 域名 或 构建时 __APP_SITE__=audit 才进入分支
// 原版 shop.dtuitui.com 构建 & 运行时：host 不匹配 + 变量未定义 → 整段逻辑不执行
function __isAuditSite(): boolean {
  try {
    const hostOK = typeof window !== 'undefined' && window.location?.hostname === 'x.dtuitui.com';
    // @ts-ignore: define by Vite audit build only, safe fallback via typeof
    const flagOK = typeof __APP_SITE__ !== 'undefined' && (__APP_SITE__ as string) === 'audit';
    return hostOK || flagOK;
  } catch (_) { return false; }
}
const AUDIT_DEMO_USER = 'audit_demo@dtuitui.com';
const AUDIT_DEMO_PASS = 'DtuiTui@2026-Demo';
const AUDIT_TOAST_MSG = '演示模式暂不支持保存';

// ==================== 审核专用：崩溃可视兜底（白屏 → 显示错误面板） ====================
// 只在 x.dtuitui.com 或 __APP_SITE__=audit 时启用；原版 shop.dtuitui.com 完全不执行
let __auditCrashPanelInstalled = false;
function __auditInstallCrashPanels(): void {
  if (!__isAuditSite()) return;
  if (__auditCrashPanelInstalled) return;
  __auditCrashPanelInstalled = true;
  const renderPanel = (title: string, body: string) => {
    try {
      let box = document.getElementById('__audit_crash_panel__');
      if (!box) {
        box = document.createElement('div');
        box.id = '__audit_crash_panel__';
        box.setAttribute('style', [
          'position:fixed;inset:0;background:#fffbe6;color:#b42318;z-index:999999;',
          'padding:20px 24px;font-family:-apple-system,"Segoe UI",sans-serif;',
          'white-space:pre-wrap;word-break:break-all;font-size:13px;line-height:1.6;',
          'border-top:6px solid #e34d59;overflow:auto;'
        ].join(''));
        // 尽量附在 #root 的父容器或 body 上
        const root = document.getElementById('root');
        (root?.parentNode as HTMLElement | null)?.appendChild?.(box) || document.body.appendChild(box);
      }
      box.innerHTML = '<div style="font-size:16px;font-weight:700;margin-bottom:8px;color:#b42318">' +
        escapeHtml(title) + '</div>' +
        '<div style="font-size:12px;color:#475467;margin-bottom:12px">请把下面的错误内容复制给开发人员，便于修复。</div>' +
        '<pre style="margin:0;background:#fef3c7;padding:12px;border-radius:8px;border:1px dashed #d97706">' + escapeHtml(body) + '</pre>';
    } catch (_) {}
  };
  const escapeHtml = (s: any) => String(s ?? '').replace(/[&<>"']/g, c => (
    c === '&' ? '&amp;' : c === '<' ? '&lt;' : c === '>' ? '&gt;' : c === '"' ? '&quot;' : '&#39;'
  ));
  window.addEventListener('error', (ev: any) => {
    const msg = [
      ev.message || ev.error?.message || 'unknown',
      '  source: ' + (ev.filename || '-'),
      '  line  : ' + (ev.lineno ?? '-') + ':' + (ev.colno ?? '-'),
      ev.error?.stack ? ('\nSTACK:\n' + ev.error.stack) : ''
    ].join('\n');
    console.error('[AUDIT-CRASH] window.error:', msg);
    renderPanel('系统崩溃（Error #310 / hooks 数量不一致）— 全局 window.onerror 捕获', msg);
  });
  window.addEventListener('unhandledrejection', (ev: any) => {
    const r = ev.reason;
    const msg = [
      'Unhandled Promise Rejection: ' + (r?.message || String(r)),
      r?.stack ? ('\nSTACK:\n' + r.stack) : ''
    ].join('\n');
    console.error('[AUDIT-CRASH] unhandledrejection:', msg);
    renderPanel('未处理的 Promise 错误（unhandledrejection）', msg);
  });
  console.info('[AUDIT] 全局崩溃面板已注入（仅审核端）');
}
// 审核专用 ErrorBoundary：把 React 树崩溃（#310 hooks 数量不一致等）渲染成可见面板
class __AuditErrorBoundary extends Component<{ children: ReactNode }, { err: Error | null; info: string | null }> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { err: null, info: null };
  }
  static getDerivedStateFromError(err: Error) {
    return { err, info: null };
  }
  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[AUDIT-CRASH] ErrorBoundary componentDidCatch:', error, info);
    // 把 componentStack 持久化到 state（下次 render 显示）
    this.setState({ info: info.componentStack || null });
    // 同时走原生兜底面板（双保险，即使 ErrorBoundary 的 JSX 渲染也崩）
    try {
      const body =
        'MESSAGE: ' + (error?.message || 'unknown') + '\n' +
        'STACK:\n' + (error?.stack || '') +
        (info?.componentStack ? ('\nRENDER STACK:\n' + info.componentStack) : '');
      const panel = document.getElementById('__audit_crash_panel__') || (() => {
        const d = document.createElement('div');
        d.id = '__audit_crash_panel__';
        d.setAttribute('style', 'position:fixed;inset:0;z-index:999999;background:#fffbe6;color:#b42318;padding:20px;overflow:auto;white-space:pre-wrap;word-break:break-all;border-top:6px solid #e34d59');
        (document.getElementById('root')?.parentNode as HTMLElement | null)?.appendChild?.(d) || document.body.appendChild(d);
        return d;
      })();
      panel.innerHTML =
        '<div style="font-size:16px;font-weight:700;margin-bottom:8px;color:#b42318">React 渲染崩溃（ErrorBoundary 捕获）</div>' +
        '<div style="font-size:12px;color:#475467;margin-bottom:12px">通常由 #310="hooks 数量不一致"或深层字段 undefined 解构导致。</div>' +
        '<pre style="margin:0;background:#fef3c7;padding:12px;border-radius:8px;border:1px dashed #d97706">' +
        String(body).replace(/[&<>"']/g, (c: any) =>
          c === '&' ? '&amp;' : c === '<' ? '&lt;' : c === '>' ? '&gt;' : c === '"' ? '&quot;' : '&#39;') +
        '</pre>';
    } catch (_) {}
  }
  render() {
    if (this.state.err == null) {
      return this.props.children as any;
    }
    // 不经过任何自定义组件（避免再次 #310），纯原生元素
    const e = this.state.err;
    const info = this.state.info || '';
    return (
      <div style={{ position: 'fixed', inset: 0, zIndex: 999998, background: '#fffbe6', color: '#b42318', padding: 20, overflow: 'auto' }}>
        <h2 style={{ margin: 0, fontSize: 16, color: '#b42318' }}>React 渲染崩溃（ErrorBoundary 已捕获，白屏已替换）</h2>
        <p style={{ fontSize: 12, color: '#475467', margin: '4px 0 12px' }}>通常由 #310 = "Rendered more hooks than during the previous render."（条件里调用 hooks）导致。请把下方内容复制给开发。</p>
        <pre style={{ background: '#fef3c7', padding: 12, borderRadius: 8, border: '1px dashed #d97706', margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all', lineHeight: 1.6, fontSize: 12 }}>
{'MESSAGE: ' + (e?.message || 'unknown')}
{'\nNAME: ' + (e?.name || 'Error')}
{'\nFILE: ' + ((e as any)?.fileName || ((e as any)?.stack?.split?.('\n')?.[0] || ''))}
{'\n\nSTACK:\n' + (e?.stack || '')}
{info ? ('\n\nRENDER COMPONENT STACK:\n' + info) : ''}
        </pre>
      </div>
    ) as any;
  }
}

function App() {
  // 【审核专用】最早时机：挂全局 window.onerror/unhandledrejection
  //（原版 shop.dtuitui.com 会在函数内部立即 return，零副作用）
  __auditInstallCrashPanels();

  const [updateInfo, setUpdateInfo] = useState<{ version: string; releaseNotes?: string } | null>(null);
  const [updateProgress, setUpdateProgress] = useState(0);
  const [updateDownloaded, setUpdateDownloaded] = useState(false);
  const [updateError, setUpdateError] = useState<string | null>(null);

  // ====== 审核专用：自动登录 + 写操作前端提示 + 隐藏登录/注册/退出入口 ======
  useEffect(() => {
    if (!__isAuditSite()) return; // 原版始终不进入本分支

    const TOKEN_KEY = 'remote_token';
    // 写齐 AuthProvider 判定 signedIn 所需的三件套：
    //   1) localStorage.admin_logged_in = '1'
    //   2) sessionStorage.auth_user = 完整 AuthUser JSON（必须带 role/permissions/employeeId/merchant_id/name）
    //   3) localStorage.remote_token + remote_token_expiry
    const persistAuditAuth = (token: string, backendUser: any) => {
      const u = backendUser || {};
      const roleClient = (u.role === 'admin' || u.role === 'merchant') ? 'admin'
        : (u.role === 'leader' ? 'leader' : 'staff');
      const authUser = {
        employeeId: String(u.employee_no || u.employeeId || u.employee_id || u.id || u.username || 'DE0001'),
        employee_no: u.employee_no || undefined,
        merchant_id: u.merchant_id || undefined,
        name: u.name || u.displayName || u.username || '抖推推演示商家审核专用',
        role: roleClient,
        groupId: u.group_id || u.groupId || null,
        group_id: u.group_id || u.groupId || null,
        permissions: u.permissions || null,
        merchantConfig: u.merchantConfig || null,
      };
      localStorage.setItem(TOKEN_KEY, token);
      localStorage.setItem('remote_token_expiry', String(Date.now() + 7 * 86400 * 1000));
      localStorage.setItem('admin_logged_in', '1'); // ← 关键：满足 hasAdminFlag
      sessionStorage.setItem('auth_user', JSON.stringify(authUser)); // ← 关键：restored=true
    };

    // 1) 若当前未登录，自动调用演示账号登录接口；
    //    登录成功后先持久化三件套（同步写完再 reload），
    //    这样 AuthProvider.useEffect 初始化时 hasAdminFlag && restored=true → signedIn=true
    const tryAutoLogin = async (force = false) => {
      const alreadySigned =
        (localStorage.getItem('admin_logged_in') === '1') &&
        !!sessionStorage.getItem('auth_user') &&
        !!localStorage.getItem(TOKEN_KEY);
      if (!force && alreadySigned) return;
      try {
        const resp = await fetch('/api/login', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ username: AUDIT_DEMO_USER, password: AUDIT_DEMO_PASS }),
        });
        const data = await resp.json();
        if (resp.ok && data.token) {
          persistAuditAuth(data.token, data.user);
          // 清掉 /login 重定向 hash → 落到首页 /#/
          const hash = window.location.hash || '';
          if (hash.startsWith('#/login') || hash === '') {
            window.location.hash = '#/';
          }
          // 仅在 "之前没有任何 token/admin 标记" 时才 reload（避免每次进入页面都抖一次）
          if (!alreadySigned) {
            window.location.reload();
          }
        }
      } catch (e) {
        console.error('[AUDIT] auto login failed', e);
      }
    };
    // 立即尝试一次
    tryAutoLogin(false);
    // 兜底：1.5s 后如果 signedIn 标记仍未齐，再重试一次（避免首屏被某个组件误清 token）
    const t = setTimeout(() => tryAutoLogin(true), 1500);

    // 2) 屏蔽退出登录导致的 认证三件套 删除操作：
    //    TOKEN_KEY / remote_token_expiry / admin_logged_in  + sessionStorage.auth_user
    const AUDIT_KEYS_LOCAL = new Set([TOKEN_KEY, 'remote_token_expiry', 'admin_logged_in']);
    const AUDIT_KEYS_SESSION = new Set(['auth_user', 'workwx_userid']);
    const origRemoveItem = Storage.prototype.removeItem;
    Storage.prototype.removeItem = function (this: Storage, key: string) {
      if (__isAuditSite()) {
        const isLocal = this === (window as any).localStorage;
        const blocked = isLocal ? AUDIT_KEYS_LOCAL.has(key) : AUDIT_KEYS_SESSION.has(key);
        if (blocked) {
          console.warn('[AUDIT] removeItem blocked:', key);
          return;
        }
      }
      return origRemoveItem.call(this, key);
    };
    const origClear = Storage.prototype.clear;
    Storage.prototype.clear = function (this: Storage) {
      if (__isAuditSite()) {
        const isLocal = this === (window as any).localStorage;
        // 保留审核三件套 + workwx_userid
        const snap = (() => {
          if (isLocal) {
            const t = localStorage.getItem(TOKEN_KEY);
            const e = localStorage.getItem('remote_token_expiry');
            const a = localStorage.getItem('admin_logged_in');
            return () => {
              if (t) localStorage.setItem(TOKEN_KEY, t);
              if (e) localStorage.setItem('remote_token_expiry', e);
              if (a) localStorage.setItem('admin_logged_in', '1');
            };
          } else {
            const u = sessionStorage.getItem('auth_user');
            const w = sessionStorage.getItem('workwx_userid');
            return () => {
              if (u) sessionStorage.setItem('auth_user', u);
              if (w) sessionStorage.setItem('workwx_userid', w);
            };
          }
        })();
        origClear.call(this);
        snap();
        return;
      }
      return origClear.call(this);
    };

    // 3) 全局屏蔽 AuthProvider 的 logout()（它会写 window.location.hash='#/login'，导致白屏抖动）
    //    通过 延迟把 window.location.hash 改回 #/ 拦截：对 hash=#/login 的请求立即覆盖为 #/
    //    （上面的 blockLoginHash 已做，这里再把 logout 函数打桩更稳）
    const anyWin = window as any;
    // AuthProvider 的 logout 会被 useAuth() 透传，我们无法直接改 context，但可以：
    //   a) 监控到 hash 变成 login 立即覆盖；
    //   b) 若任何逻辑调用 localStorage.removeItem('admin_logged_in') 后马上写回来（上面 removeItem 已拦）
    //   c) 兜底：50ms tick 检查 admin_logged_in 是否被删，若是就从已存的 token 重新 persist（上面 removeItem 已拦，不需再 tick）
    void anyWin;

    // 3) 路由守卫：任何进入 #/login 的 hash 跳转都重定向回 #/ （去掉登录入口可见性）
    const blockLoginHash = () => {
      const h = window.location.hash || '';
      if (h.startsWith('#/login')) {
        window.location.hash = '#/';
      }
    };
    window.addEventListener('hashchange', blockLoginHash);
    blockLoginHash();

    // 4) 拦截 fetch/XHR：后端已写 no-op 返回；前端再统一弹"演示模式"提示，便于审核理解
    const orFetch = window.fetch;
    window.fetch = async function (input: RequestInfo | URL, init?: RequestInit) {
      const resp = await orFetch.apply(this, [input as any, init]);
      try {
        const method = (init?.method || (typeof input === 'string' ? 'GET' : (input as Request).method) || 'GET').toUpperCase();
        const write = ['POST','PUT','DELETE','PATCH'].includes(method);
        if (!write) return resp;
        // 只克隆/解析 JSON 类型响应，避免解析二进制出错
        const ctype = resp.headers.get('content-type') || '';
        if (!ctype.includes('application/json')) return resp;
        const cloned = resp.clone();
        const body = await cloned.json();
        if (body && body.__auditNoop === true) {
          // 使用 Toast 组件的事件派发表单提示
          try {
            const ev = new CustomEvent('audit:noop:toast', { detail: { msg: body.message || AUDIT_TOAST_MSG } });
            window.dispatchEvent(ev);
          } catch (_) {}
        }
      } catch (_) {}
      return resp;
    };

    // 5) 前端写操作兜底：在请求发出去之前就拦掉(针对非/api路径 & 部分特殊路径，防前端表单直接抛错)
    // 已由后端 no-op 覆盖；此处作为 UX 增强只做提示，不改变响应语义

    return () => {
      clearTimeout(t);
      window.removeEventListener('hashchange', blockLoginHash);
      Storage.prototype.removeItem = origRemoveItem;
      Storage.prototype.clear = origClear;
      window.fetch = orFetch;
    };
  }, []);

  // ====== 审核专用：监听 __auditNoop toast 事件弹提示 ======
  useEffect(() => {
    if (!__isAuditSite()) return;
    const handler = (e: Event) => {
      const msg = (e as CustomEvent).detail?.msg || AUDIT_TOAST_MSG;
      // 通过页面内的 toast 全局容器 API 提示；如未就绪则降级为原生 alert
      try {
        const anyWin = window as any;
        if (anyWin.__toastShow) { anyWin.__toastShow(msg); return; }
        if (anyWin.Toast && anyWin.Toast.show) { anyWin.Toast.show(msg); return; }
        // 最后兜底：dispatch 一个 React 可捕获的事件，下面的 AuditToastRelay 组件会接
        const ev = new CustomEvent('audit:toast:show', { detail: { msg } });
        window.dispatchEvent(ev);
      } catch (_) { alert(msg); }
    };
    window.addEventListener('audit:noop:toast', handler as any);
    return () => window.removeEventListener('audit:noop:toast', handler as any);
  }, []);

  useEffect(() => {
    const api = (typeof runtime !== "undefined" ? (runtime._electronApi || (window as any).electronAPI || {}) : (window as any).electronAPI || {});
    if (!api) return;
    api.onUpdateAvailable((info: any) => {
      setUpdateInfo(info);
      setUpdateError(null);
    });

    api.onUpdateNotAvailable(() => {
      console.log('当前已是最新版本');
    });

    api.onUpdateDownloadProgress((progress: any) => {
      setUpdateProgress(Math.round(progress.percent || 0));
    });

    api.onUpdateDownloaded(() => {
      setUpdateDownloaded(true);
    });

    api.onUpdateError((msg: string) => {
      setUpdateError(msg);
    });

    const checkForUpdates = async () => {
      try {
        await api.checkForUpdates();
      } catch (e) {
        console.error('检查更新失败:', e);
      }
    };

    setTimeout(checkForUpdates, 3000);
  }, []);

  const handleDownload = async () => {
    const api = (typeof runtime !== "undefined" ? (runtime._electronApi || (window as any).electronAPI || {}) : (window as any).electronAPI || {});
    if (!api) return;
    try {
      await api.downloadUpdate();
    } catch (e: any) {
      setUpdateError(e.message);
    }
  };

  const handleInstall = () => {
    const api = (typeof runtime !== "undefined" ? (runtime._electronApi || (window as any).electronAPI || {}) : (window as any).electronAPI || {});
    if (api) api.installUpdate();
  };

  const routerTree = (
    <Router>
      <H5MockPageRouter />
      <Toast />
      <UpdateModal
        updateInfo={updateInfo}
        progress={updateProgress}
        downloaded={updateDownloaded}
        error={updateError}
        onDownload={handleDownload}
        onInstall={handleInstall}
        onClose={() => setUpdateInfo(null)}
      />
      {/* FIXED: 包裹 AuthProvider，为人员管理权限系统提供全局上下文 */}
      <AuthProvider>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* ========= H5 真实移动端路由（企微登录后，真实 API 调用，5-Tab 导航） ========= */}
            <Route path="/h5/workbench" element={<H5Shell><Suspense fallback={<PageLoader />}><H5WorkbenchPage /></Suspense></H5Shell>} />
            <Route path="/h5/samples"   element={<H5Shell><Suspense fallback={<PageLoader />}><H5SamplesPage /></Suspense></H5Shell>} />
            <Route path="/h5/approval"  element={<H5Shell><Suspense fallback={<PageLoader />}><H5ApprovalPage /></Suspense></H5Shell>} />
            <Route path="/h5/data"      element={<H5Shell><Suspense fallback={<PageLoader />}><H5DataPage /></Suspense></H5Shell>} />
            <Route path="/h5/profile"   element={<H5Shell><Suspense fallback={<PageLoader />}><H5ProfilePage /></Suspense></H5Shell>} />
            {/* H5 真实绑定页（企微 OAuth 未绑定时进入） */}
            <Route path="/h5/bind" element={<Suspense fallback={<PageLoader />}><H5BindPage /></Suspense>} />
            {/* H5 Mock 页面（企微审核截图专用，纯静态，保留兼容） */}
            <Route path="/h5/mock/workbench" element={<H5MockShell><MockWorkbenchPage /></H5MockShell>} />
            <Route path="/h5/mock/approval"  element={<H5MockShell><MockApprovalPage  /></H5MockShell>} />
            <Route path="/h5/mock/settings"  element={<H5MockShell><MockSettingsPage  /></H5MockShell>} />
            {/* 旧 /h5/settings 重定向到 /h5/profile */}
            <Route path="/h5/settings" element={<Navigate to="/h5/profile" replace />} />
            {/* 旧 page=xxx 参数路由兼容 */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/" element={<ProtectedLayout><HomePage /></ProtectedLayout>} />
            {/* FIXED: 达人信息拆成两个子路由；旧路径 /influencers 重定向到列表页 */}
            <Route path="/influencers" element={<Navigate to="/influencers/list" replace />} />
            <Route path="/influencers/list" element={<ProtectedLayout><InfluencersPage /></ProtectedLayout>} />
            <Route path="/influencers/blacklist" element={<ProtectedLayout><InfluencerBlacklistPage /></ProtectedLayout>} />
            <Route path="/influencers/global-blacklist" element={<ProtectedLayout><GlobalBlacklistPage /></ProtectedLayout>} />
            {/* FIXED: 数据管理4个路由；/data-management 重定向到默认子页 */}
            <Route path="/data-management" element={<Navigate to="/data-management/staff-samples" replace />} />
            <Route path="/data-management/staff-samples" element={<ProtectedLayout><StaffSamplesPage /></ProtectedLayout>} />
            <Route path="/data-management/workgroup-samples" element={<ProtectedLayout><WorkgroupSamplesPage /></ProtectedLayout>} />
            <Route path="/data-management/express-status" element={<ProtectedLayout><ExpressStatusPage /></ProtectedLayout>} />
            <Route path="/data-management/sample-progress-data" element={<ProtectedLayout><SampleProgressDataPage /></ProtectedLayout>} />
          <Route path="/data-management/investment-dashboard" element={<ProtectedLayout><InvestmentDashboardPage /></ProtectedLayout>} />
          <Route path="/data-management/employee-performance" element={<ProtectedLayout><EmployeePerformancePage /></ProtectedLayout>} />
          {/* 第三期：工作组预算管理（仅管理员） */}
          <Route path="/data-management/budget" element={<ProtectedLayout><BudgetManagePage /></ProtectedLayout>} />
            <Route path="/products" element={<ProtectedLayout><ProductsPage /></ProtectedLayout>} />
            <Route path="/products/poster" element={<ProtectedLayout><PosterPage /></ProtectedLayout>} />
            <Route path="/products/handcard" element={<ProtectedLayout><HandCardPage /></ProtectedLayout>} />
            <Route path="/express" element={<ProtectedLayout><ExpressPage /></ProtectedLayout>} />
            {/* 2026-08：发样管理新路径（兼容旧路径书签/H5入口） */}
            <Route path="/samples"                  element={<Navigate to="/sample-management/samples"  replace />} />
            <Route path="/approval"                 element={<Navigate to="/sample-management/approval" replace />} />
            <Route path="/sample-returns"           element={<Navigate to="/sample-management/returns"  replace />} />
            <Route path="/sample-management/samples"  element={<ProtectedLayout><SamplesPage /></ProtectedLayout>} />
            <Route path="/sample-management/approval" element={<ProtectedLayout><ApprovalWorkbenchPage /></ProtectedLayout>} />
            <Route path="/sample-management/returns"  element={<ProtectedLayout><SampleReturnPage /></ProtectedLayout>} />
            {/* 打单工作台 */}
            <Route path="/printing" element={<ProtectedLayout><PrintingWorkbenchPage /></ProtectedLayout>} />
            {/* FIXED: 样品进度路由 */}
            <Route path="/sample-progress" element={<ProtectedLayout><SampleProgressPage /></ProtectedLayout>} />
            {/* 第三期：全局风险预警中心 */}
            <Route path="/risk-alerts" element={<ProtectedLayout><RiskAlertPage /></ProtectedLayout>} />
            <Route path="/init-db" element={<ProtectedLayout><InitDB /></ProtectedLayout>} />
            {/* ========== 新容器页路由 ========== */}
            <Route path="/settings/account" element={<ProtectedLayout><AccountManagePage /></ProtectedLayout>} />
            <Route path="/settings/messages" element={<ProtectedLayout><MessageCenterPage /></ProtectedLayout>} />
            <Route path="/settings/system-ops" element={<ProtectedLayout><SystemOpsPage /></ProtectedLayout>} />
            <Route path="/settings/fee" element={<ProtectedLayout><FeePage /></ProtectedLayout>} />
            {/* ========== 旧路由重定向到新容器页（防书签失效） ========== */}
            <Route path="/settings/staff" element={<Navigate to="/settings/account?tab=staff" replace />} />
            <Route path="/settings/workgroups" element={<Navigate to="/settings/account?tab=workgroups" replace />} />
            <Route path="/settings/permissions" element={<Navigate to="/settings/account?tab=permissions" replace />} />
            <Route path="/settings/account-bind" element={<Navigate to="/settings/account?tab=bind" replace />} />
            <Route path="/settings/webhook" element={<Navigate to="/settings/messages?tab=webhook" replace />} />
            <Route path="/settings/templates" element={<Navigate to="/settings/messages?tab=template" replace />} />
            <Route path="/settings/sms" element={<Navigate to="/settings/messages?tab=sms" replace />} />
            <Route path="/settings/notification" element={<Navigate to="/settings/messages?tab=notification" replace />} />
            <Route path="/settings/audit-logs" element={<Navigate to="/settings/system-ops" replace />} />
            <Route path="/settings/system" element={<Navigate to="/settings/system-ops" replace />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </AuthProvider>
    </Router>
  );

  // 【审核专用】条件包裹 ErrorBoundary：仅审核端启用（崩溃 → 可视面板，非白屏）；原版返回裸树
  if (__isAuditSite()) {
    return <__AuditErrorBoundary>{routerTree}</__AuditErrorBoundary> as any;
  }
  return routerTree;
}

export default App;